import { api } from '@/api';

// Response from creating a document record: a signed PUT URL plus the new document id. Some
// backends also return the resolved file extension.
export interface CreateAttachmentResponse {
  documentId: string;
  uploadUrl: string;
  extension?: string;
}

export interface CreateAttachmentRequest {
  name: string;
  mimeType: string;
}

// createEntityAttachmentApi builds the three entity-attachment endpoints for one owner. The
// basePath is everything before `/documents`, so it works for both flat and nested owners, e.g.
// `/v1/service-contracts/${contractId}` or `/v1/deviceModels/${modelId}/firmwares/${firmwareUuid}`.
export function createEntityAttachmentApi(basePath: string) {
  const documentsPath = `${basePath}/documents`;
  return {
    // Creates the document record and returns its signed upload URL.
    create(request: CreateAttachmentRequest) {
      return api.post<CreateAttachmentResponse>(documentsPath, request);
    },
    // Marks the uploaded object as available after the signed-URL upload; T is the returned
    // document, or void when the backend answers with an empty body.
    enable<T = void>(documentId: string) {
      return api.put<T>(`${documentsPath}/${documentId}/enable`);
    },
    // Soft-deletes the document.
    remove(documentId: string) {
      return api.delete<void>(`${documentsPath}/${documentId}`);
    },
  };
}

export type EntityAttachmentApi = ReturnType<typeof createEntityAttachmentApi>;

// uploadToSignedUrl PUTs the raw file bytes to a presigned storage URL. It bypasses the api
// client on purpose: that client always prepends the API base URL and sends cookies plus a JSON
// content type, none of which apply to an external signed URL. fallbackMimeType covers files
// whose type is empty. When an onProgress callback is given the upload runs through
// XMLHttpRequest to report byte progress; otherwise a plain fetch is used.
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  fallbackMimeType = 'application/octet-stream',
  onProgress?: (progress: number) => void
): Promise<void> {
  const contentType = file.type || fallbackMimeType;
  if (onProgress) {
    return uploadWithProgress(uploadUrl, file, contentType, onProgress);
  }

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }
}

// uploadWithProgress performs the signed-URL PUT via XMLHttpRequest so the upload progress
// events can drive a percentage callback the plain fetch API does not expose.
function uploadWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (progress: number) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Content-Type', contentType);

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`Upload failed: ${request.status} ${request.statusText}`));
    };

    request.onerror = () => reject(new Error('Upload failed'));
    request.send(file);
  });
}

// uploadEntityAttachment runs the full three-step flow: create the record, PUT the bytes to the
// signed URL, then enable the document. Name and mime type default from the file; callers can
// override the name (firmware uses a fixed 'firmware.bin'). An optional onProgress callback
// reports the byte upload progress and returns the new document id so callers can complete an
// exchange by removing the previous document.
export async function uploadEntityAttachment(
  attachments: EntityAttachmentApi,
  options: {
    file: File;
    name?: string;
    mimeType?: string;
    fallbackMimeType?: string;
    onProgress?: (progress: number) => void;
  }
): Promise<string> {
  const {
    file,
    name,
    mimeType,
    fallbackMimeType = 'application/octet-stream',
    onProgress,
  } = options;
  const { uploadUrl, documentId } = await attachments.create({
    name: name ?? file.name,
    mimeType: mimeType ?? file.type ?? fallbackMimeType,
  });
  await uploadToSignedUrl(uploadUrl, file, fallbackMimeType, onProgress);
  await attachments.enable(documentId);
  return documentId;
}
